import { Frow, ISelectOption } from '@pinpointhq/thumbtack';
import * as React from 'react';
import { useCompanySettings } from '../../../javascript/application/useCompanySettings';
import {
  MapboxAddress,
  MapboxPlaceAutoComplete,
  extractAddressComponents,
} from '../../admin/shared/MapboxPlaceAutoComplete';
import { isoCountries } from '../iso2_countries';
import SelectField from './SelectField';
import { FallbackAddressForm } from './addresses/FallbackAddressForm';
import { UsAddressForm } from './addresses/UsAddressForm';
import { IAddressFieldNames, IAddressTranslations, IStoredAttributes } from './addresses/interfaces';

interface IAddress {
  countryOptions: ISelectOption[];
  fieldNames: IAddressFieldNames;
  stateOptions: ISelectOption[];
  translations: IAddressTranslations;
  storedAttributes: IStoredAttributes;
  required?: boolean;
}

export default function Address({
  fieldNames,
  countryOptions,
  required = true,
  stateOptions,
  storedAttributes,
  translations,
}: IAddress) {
  let detectedCountry;
  // I'm worried about this breaking and stopping people submitting the form so using a dirty try catch to make sure
  // it doesn't brick the component
  try {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    detectedCountry = isoCountries[timeZone];
  } catch {
    detectedCountry = null;
  }
  const [attributes, setAttributes] = React.useState<IStoredAttributes>(storedAttributes);
  const { isRadiusSearchEnabled } = useCompanySettings();
  const [country, setCountry] = React.useState(attributes.country || detectedCountry);
  const [selectedOption, setSelectedOption] = React.useState<ISelectOption | null>(null);

  const handleSelect = (selectedOption) => {
    if (selectedOption) {
      setCountry(selectedOption.value);
    } else {
      setCountry(null);
    }
  };

  const components = {
    US: UsAddressForm,
  };

  const Component = components[country] || FallbackAddressForm;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    e.persist();

    setAttributes((prevValues) => ({
      ...prevValues,
      // NOTE: (VR) Causes an error when Search Radius is not enabled!
      [key]: e.target?.value,
    }));
  };

  const handlePlaceSelect = (place: MapboxAddress | null) => {
    if (!place) return;

    const {
      autocompletedAddressOne,
      autocompletedAddressTwo,
      autocompletedTown,
      autocompletedPostcode,
      autocompletedProvince,
      autocompletedCountry,
    } = extractAddressComponents(place, country === 'US');

    setAttributes({
      addressOne: autocompletedAddressOne || '',
      addressTwo: autocompletedAddressTwo || '',
      country: autocompletedCountry || '',
      town: autocompletedTown || '',
      postcode: autocompletedPostcode || '',
      province: autocompletedProvince || '',
    } as IStoredAttributes);
  };

  return (
    <Frow verticalGutters={32}>
      <div className="col-1-1" id="address-country">
        <label className="external-form__label external-form__label--required">{translations.country}</label>
        <SelectField
          name={fieldNames.country}
          options={countryOptions}
          storedOption={country}
          handleSelect={handleSelect}
          placeholder={translations.countryPlaceholder}
        />
      </div>
      {isRadiusSearchEnabled && (
        <div className="col-1-1" id="google-places-autocomplete">
          <label className="external-form__label">{translations.addressAutocomplete}</label>
          <MapboxPlaceAutoComplete
            onPlaceSelect={handlePlaceSelect}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            placeholder={translations.addressAutocompletePlaceholder}
            restrictedCountry={country}
          />
        </div>
      )}
      <Component
        required={required}
        fieldNames={fieldNames}
        stateOptions={stateOptions}
        attributes={attributes}
        handleChange={handleChange}
        translations={translations}
      />
    </Frow>
  );
}
